import { Flex } from 'antd';
import CardDetailButton from '../cardDetailButton/cardDetailButton';
import InteractiveCard from '../common/interactiveCard';
import { Divider, Heading, Paragraph } from '../Ant';
import { Briefcase, Calendar } from '../icons';
import { useTranslation } from 'react-i18next';
import CardData from '../dashboard/cardData';
import { InvoiceableType, MatterStatus } from '../../constants/matter';
import { PreliminaryInvoiceStatus } from '../../interface/invoice';
import {
  MatterBorderColor,
  MatterTagColor,
  PreliminaryInvoiceStatusBorderColor,
  PreliminaryInvoiceStatusTagColor,
  renderInvoiceStatus,
} from '../../constants/invoice';
import {
  formatter,
  getLocalDateAndTime,
  getMatterIconClass,
  getMatterLabel,
} from '../../constants/utilityFunctions';
import { useTransactionParameters } from '../../context/TransactionParameterContext';
import NoteIcons from '../common/NoteIcons';

interface IInvoicingMatterCard {
  subject: string;
  number: number;
  matterInvoiceableType: InvoiceableType;
  customer: string;
  startDate?: string;
  endDate?: string;
  amount: number;
  amountInCurrency: number;
  currency?: string;
  matterStatus?: MatterStatus;
  invoiceStatus?: PreliminaryInvoiceStatus;
  onSelect?: () => void;
  hasImportantNotes?: boolean;
  hasImportantRelatedNotes?: boolean;
  hasNotes?: boolean;
}
const InvoiceMatterCard = ({
  subject,
  number,
  customer,
  startDate,
  endDate,
  amount,
  amountInCurrency,
  currency,
  invoiceStatus,
  matterStatus,
  onSelect,
  hasImportantNotes,
  hasImportantRelatedNotes,
  matterInvoiceableType,
  hasNotes,
}: IInvoicingMatterCard) => {
  const { t } = useTranslation();
  const { parameters } = useTransactionParameters();
  const baseCurrency = parameters?.currency;
  return (
    <InteractiveCard
      className={`pointer w-full conflict-card ${matterStatus && MatterBorderColor[matterStatus]} ${invoiceStatus && PreliminaryInvoiceStatusBorderColor[invoiceStatus]}`}
      onActivate={onSelect}
      data-testid="invoice-matter-card"
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={8} align="center">
          <Flex
            data-testid="invoice-matter-card-status-chip"
            justify="center"
            align="center"
            className={`status-chip ${matterStatus && MatterTagColor[matterStatus]} ${invoiceStatus && PreliminaryInvoiceStatusTagColor[invoiceStatus]}`}
          >
            {renderInvoiceStatus(invoiceStatus, t, matterStatus)}
          </Flex>
          <NoteIcons
            hasImportantNotes={hasImportantNotes}
            hasImportantRelatedNotes={hasImportantRelatedNotes}
            hasNotes={hasNotes}
          />
        </Flex>
        <Flex gap={10}>
          {onSelect && (
            <CardDetailButton onOpen={onSelect} testId="invoice-matter-card-go-detail" />
          )}
        </Flex>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10}>
        <CardData
          type="matter"
          icon={<Briefcase className={getMatterIconClass(matterInvoiceableType ?? '')} />}
          title={getMatterLabel(t, matterInvoiceableType)}
          content={customer}
          subcontent={`${number} - ${subject}`}
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-1"
        />
        <CardData
          icon={<Calendar />}
          type="transaction"
          title={t('preliminaryInvoice:period')}
          content={
            startDate && endDate
              ? `${getLocalDateAndTime(new Date(startDate), true)} - ${getLocalDateAndTime(new Date(endDate), true)}`
              : '-'
          }
        />
        <Divider />
        <Flex justify="space-between">
          <Paragraph
            data-testid="invoice-matter-card-total-net-amount"
            $fontsize="13px"
            $weight={500}
            $color="title"
            $lineheight="20px"
          >
            {`${t('matter:totalNetAmount')} (${currency ?? 'EUR'})`}
          </Paragraph>
          <Heading
            data-testid="invoice-matter-card-total-net-amount-value"
            level={6}
            $fontsize="13px"
            $weight={700}
            $color="title"
            $lineheight="20px"
          >
            {`${formatter.regionalNumberFormat({
              value: baseCurrency === currency ? (amount ?? 0) : (amountInCurrency ?? 0),
              minimumFractionDigits: 2,
            })}`}
          </Heading>
        </Flex>
      </Flex>
    </InteractiveCard>
  );
};

export default InvoiceMatterCard;
